@if (feedback(); as item) {
  <div class="fb-detail-overlay" (click)="close.emit()"></div>

  <aside class="fb-detail" role="dialog" aria-label="Feedback detail">
    <!-- header -->
    <header class="fb-detail-head" [style.background]="categoryGradient(item.category)">
      <button class="fb-detail-close" type="button" (click)="close.emit()" aria-label="Close">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div class="fb-detail-tags">
        @if (item.status) {
          <fb-status-chip
            [statuses]="statuses()"
            [current]="item.status"
            (statusChange)="statusChange.emit($event)"
          />
        }
        @let type = typeStyle(item.type ?? '');
        <span class="type-pill" [style.background]="type.bg" [style.color]="type.text">{{
          type.label
        }}</span>
      </div>
      <div class="fb-detail-element">{{ item.element || '—' }}</div>
      @if (item.url) {
        <div class="fb-detail-url">{{ item.url }}</div>
      }
    </header>

    <!-- scrollable body -->
    <div class="fb-detail-body">
      <!-- created by -->
      <div class="created-by">
        @if (isAiAuthor(item)) {
          <span class="created-avatar ai" aria-hidden="true">
            <svg viewBox="0 0 40 40" fill="none">
              <line
                x1="20"
                y1="3"
                x2="20"
                y2="9"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
              />
              <circle cx="20" cy="3" r="2.4" fill="#ec1c7a" />
              <rect x="7" y="9" width="26" height="22" rx="6" fill="white" />
              <rect x="10" y="13" width="20" height="13" rx="3" fill="#0f1e3d" />
              <circle cx="16" cy="19.5" r="2" fill="#1bb6c1" />
              <circle cx="24" cy="19.5" r="2" fill="#1bb6c1" />
              <path
                d="M16 23 Q20 25.5 24 23"
                stroke="#ec1c7a"
                stroke-width="1.4"
                stroke-linecap="round"
                fill="none"
              />
            </svg>
          </span>
          <div class="created-main">
            <div class="section-label">Created By</div>
            <div class="created-name">AI Reviewer</div>
          </div>
        } @else {
          <span class="created-avatar">{{ initials(item.createdBy?.name) }}</span>
          <div class="created-main">
            <div class="section-label">Created By</div>
            <div class="created-name">{{ item.createdBy?.name || 'Unknown' }}</div>
            @if (item.createdBy?.email) {
              <div class="created-email">{{ item.createdBy?.email }}</div>
            }
          </div>
        }
        @if (item.created) {
          <div class="created-date">{{ item.created | date: 'MMM d, y, h:mm a' }}</div>
        }
      </div>

      <!-- observation -->
      <section>
        <div class="section-label">Observation</div>
        <div class="prose">{{ item.observation || 'No observation provided.' }}</div>
      </section>

      <!-- recommendation -->
      @if (item.recommendation) {
        <section>
          <div class="section-label rec">Recommendation</div>
          <div class="prose rec">{{ item.recommendation }}</div>
        </section>
      }

      <!-- reasoning -->
      @if (item.reasoning) {
        <section>
          <div class="section-label reason">Reasoning</div>
          <div class="prose reason">{{ item.reasoning }}</div>
        </section>
      }

      <!-- details grid -->
      <section>
        <div class="section-label">Details</div>
        <div class="meta-grid">
          <div class="meta-card">
            <span class="meta-label">Category</span>
            <span class="meta-value">{{ item.category ? humanize(item.category) : '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Score</span>
            <span class="meta-value score" [style.color]="scoreColor(item.score)">{{
              scoreLabel(item.score)
            }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Type</span>
            <span class="meta-value" [style.color]="typeStyle(item.type ?? '').text">{{
              typeStyle(item.type ?? '').label
            }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Browser</span>
            <span class="meta-value">{{ browserName(item.browser) || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">OS</span>
            <span class="meta-value">{{ item.os || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Device</span>
            <span class="meta-value">{{ item.device || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Resolution</span>
            <span class="meta-value">{{ item.resolution || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Browser Version</span>
            <span class="meta-value">{{ item.appVersion || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Orientation</span>
            <span class="meta-value">{{ item.orientation || '—' }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">Screen</span>
            <span class="meta-value screen">
              @if (item.screen) {
                <span
                  class="screen-ic"
                  [style.color]="screenColor(item.screen)"
                  [title]="item.screen"
                >
                  @switch (item.screen) {
                    @case ('mobile') {
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <rect x="5" y="2" width="14" height="20" rx="2" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                      </svg>
                    }
                    @case ('tablet') {
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                      </svg>
                    }
                    @default {
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <rect x="2" y="3" width="20" height="14" rx="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    }
                  }
                </span>
                <span class="screen-label">{{ item.screen }}</span>
              } @else {
                —
              }
            </span>
          </div>
        </div>
        @if (item.widgetVersion) {
          <p class="widget-version">Widget Version: {{ item.widgetVersion }}</p>
        }
      </section>

      <!-- full URL -->
      @if (item.fullUrl) {
        <section>
          <div class="section-label">Full URL</div>
          <div class="full-url-row">
            <a
              class="full-url"
              [href]="pageLink()(item.fullUrl)"
              target="_blank"
              rel="noopener noreferrer"
              title="Open in new tab"
            >
              <span class="full-url-text">{{ item.fullUrl }}</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
            <a
              class="url-tool url-tool-sizzy"
              [href]="sizzyUrl(item.fullUrl)"
              title="Open in Sizzy"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect width="24" height="24" rx="6" fill="#6c5ce7" />
                <text
                  x="12"
                  y="12"
                  text-anchor="middle"
                  dominant-baseline="central"
                  font-family="Arial, Helvetica, sans-serif"
                  font-size="16"
                  font-weight="700"
                  fill="#ffffff"
                >
                  S
                </text>
              </svg>
            </a>
            <a
              class="url-tool url-tool-bs"
              [href]="browserStackUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
              title="Open in BrowserStack (closest match)"
            >
              <img src="browserstack.svg" alt="BrowserStack" width="18" height="18" />
            </a>
          </div>
        </section>
      }

      <!-- assets -->
      @if (item.assets?.length) {
        <section>
          <div class="section-label assets-label">
            Assets<span class="assets-count">{{ item.assets?.length }}</span>
          </div>
          <div class="assets">
            @for (asset of item.assets; track asset.url) {
              <a
                class="asset"
                [href]="asset.url"
                target="_blank"
                rel="noopener noreferrer"
                [title]="'Open ' + asset.name + ' in new tab'"
              >
                <span class="asset-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </span>
                <span class="asset-name">{{ asset.name }}</span>
                <svg
                  class="asset-ext"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            }
          </div>
        </section>
      }

      <!-- screenshot -->
      @if (item.screenshotUrl) {
        <section>
          <div class="section-label">Screenshot</div>
          <a
            class="screenshot"
            [href]="item.screenshotUrl"
            target="_blank"
            rel="noopener noreferrer"
            title="Open full size"
          >
            <img [src]="item.screenshotUrl" alt="Feedback screenshot" />
            <span class="screenshot-overlay">↗ Open full size</span>
          </a>
        </section>
      }

      <!-- comments -->
      <section>
        <div class="section-label">Comments</div>
        <fb-comment-thread
          [comments]="comments()"
          [loading]="commentsLoading()"
          [posting]="commentPosting()"
          [canPostPrivate]="canPostPrivateComment()"
          [recipientOptions]="commentRecipientOptions()"
          [feedbackAuthorEmail]="commentRecipientAuthorEmail()"
          [currentUserEmail]="currentUserEmail()"
          (post)="commentPost.emit($event)"
        />
      </section>
    </div>
  </aside>
}
